import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Loader2, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  type AdminPlatformUser,
  getAdminUsers,
  getCurrentUser,
  PlatformApiError,
  type PlatformRole,
  type PlatformUser,
  updateAdminUser,
} from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/users')({
  component: PlatformUsersPage,
})

/**
 * The four values `platform_users.role` ever holds. Order is
 * deliberate — lowest privilege on the left, highest on the right.
 * Matches the backend's KNOWN_PLATFORM_ROLES enum.
 */
const ROLE_OPTIONS: Array<{ value: PlatformRole; label: string; tone: string }> = [
  {
    value: 'customer_owner',
    label: 'Customer owner',
    tone: 'bg-muted text-muted-foreground ring-border',
  },
  {
    value: 'platform_readonly',
    label: 'Read-only',
    tone: 'bg-chart-4/10 text-chart-4 ring-chart-4/25',
  },
  {
    value: 'platform_support',
    label: 'Support',
    tone: 'bg-chart-3/10 text-chart-3 ring-chart-3/25',
  },
  {
    value: 'platform_admin',
    label: 'Admin',
    tone: 'bg-primary/10 text-primary ring-primary/25',
  },
]

function PlatformUsersPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; me: PlatformUser; users: AdminPlatformUser[] }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set())
  const [mutationError, setMutationError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    // /me alongside /admin/users so the self-row check has
    // authoritative data — the parent route already gates on role,
    // but it doesn't thread the user object down. Fetching twice is
    // cheap and the two calls parallelize.
    Promise.all([getCurrentUser(ctrl.signal), getAdminUsers(ctrl.signal)])
      .then(([me, users]) => setState({ status: 'loaded', me, users }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load users'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  async function handleRoleChange(user: AdminPlatformUser, nextRole: PlatformRole) {
    if (state.status !== 'loaded') return
    if (user.role === nextRole) return

    // Optimistic swap — flip local state immediately and roll back
    // on failure. Same pattern as /platform/modules toggles.
    const prevUsers = state.users
    setState({
      status: 'loaded',
      me: state.me,
      users: prevUsers.map((u) => (u.id === user.id ? { ...u, role: nextRole } : u)),
    })
    setBusyKeys((s) => new Set(s).add(user.id))
    setMutationError(null)

    try {
      const updated = await updateAdminUser(user.id, nextRole)
      setState((s) => {
        if (s.status !== 'loaded') return s
        return {
          status: 'loaded',
          me: s.me,
          users: s.users.map((u) => (u.id === user.id ? updated : u)),
        }
      })
    } catch (err) {
      // Roll back
      setState({ status: 'loaded', me: state.me, users: prevUsers })
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : `Failed to update ${user.email}`
      setMutationError(message)
    } finally {
      setBusyKeys((s) => {
        const next = new Set(s)
        next.delete(user.id)
        return next
      })
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Platform users</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every account in{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">platform_users</code>. Change a
          role to promote or demote. You can't change your own — ask another admin if you need to
          step down.
        </p>
      </header>

      {mutationError ? (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {mutationError}
        </div>
      ) : null}

      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? <ErrorState message={state.message} /> : null}
      {state.status === 'loaded' && state.users.length === 0 ? <EmptyState /> : null}
      {state.status === 'loaded' && state.users.length > 0 ? (
        <UserTable
          users={state.users}
          currentUserId={state.me.id}
          currentUserRole={state.me.role}
          busyKeys={busyKeys}
          onRoleChange={handleRoleChange}
        />
      ) : null}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading users…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center"
    >
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
        <Users className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">No platform users</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        The <code className="rounded bg-muted px-1 py-0.5 text-xs">platform_users</code> table is
        empty. Sign someone up via{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">/sign-up</code> and they'll land here
        as a customer owner.
      </p>
    </motion.div>
  )
}

function UserTable({
  users,
  currentUserId,
  currentUserRole,
  busyKeys,
  onRoleChange,
}: {
  users: AdminPlatformUser[]
  currentUserId: string
  currentUserRole: string | null
  busyKeys: Set<string>
  onRoleChange: (user: AdminPlatformUser, role: PlatformRole) => void
}) {
  // Only platform_admin may mutate roles. Anyone else sees the
  // table but every selector is disabled — same rules as the
  // backend, mirrored client-side for clarity.
  const canMutate = currentUserRole === 'platform_admin'

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">User</th>
              <th className="px-4 py-2.5 text-left font-semibold">Role</th>
              <th className="px-4 py-2.5 text-left font-semibold">Workspaces</th>
              <th className="px-4 py-2.5 text-left font-semibold">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => {
              const isSelf = user.id === currentUserId
              const busy = busyKeys.has(user.id)
              const disabled = !canMutate || isSelf || busy
              return (
                <tr key={user.id} className="hover:bg-accent/40">
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{user.name ?? user.email}</div>
                      {isSelf ? (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary ring-1 ring-primary/25">
                          You
                        </span>
                      ) : null}
                      {!user.emailVerified ? (
                        <span className="inline-flex items-center rounded-full bg-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning ring-1 ring-warning/25">
                          Unverified
                        </span>
                      ) : null}
                    </div>
                    {user.name ? (
                      <div className="mt-0.5 text-xs text-muted-foreground">{user.email}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <RoleBadge role={user.role} />
                      {busy ? (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      ) : null}
                    </div>
                    <select
                      className={cn(
                        'mt-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs',
                        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                      )}
                      value={user.role}
                      disabled={disabled}
                      onChange={(e) => onRoleChange(user, e.target.value as PlatformRole)}
                      title={
                        !canMutate
                          ? 'Only platform_admin can change roles'
                          : isSelf
                            ? "You can't change your own role"
                            : "Change this user's role"
                      }
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                      {/* Unknown roles still render so we don't drop data */}
                      {ROLE_OPTIONS.every((o) => o.value !== user.role) ? (
                        <option value={user.role}>{user.role}</option>
                      ) : null}
                    </select>
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                    {user.tenantCount === 0 ? '—' : user.tenantCount}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                    {formatDate(user.createdAt)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

function RoleBadge({ role }: { role: string }) {
  const match = ROLE_OPTIONS.find((o) => o.value === role)
  const label = match?.label ?? role
  const tone = match?.tone ?? 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
